import type { operations } from "@tamariba/api-client";

export type LayoutSummary = Omit<
  operations["listLayouts"]["responses"][200]["content"]["application/json"][number],
  "mainLayoutId"
> & {
  mainLayoutId?: string | null;
};
export type LayoutGroup<T extends LayoutSummary = LayoutSummary> = {
  main: T;
  subs: T[];
};

function timestamp(value: string) {
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : 0;
}
function compareId(a: LayoutSummary, b: LayoutSummary) {
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function groupLayouts<T extends LayoutSummary>(
  layouts: readonly T[],
  query = "",
): LayoutGroup<T>[] {
  const byId = new Map<string, T>();
  for (const layout of layouts) {
    const id = layout.id.toLowerCase();
    const previous = byId.get(id);
    if (
      !previous ||
      timestamp(layout.updatedAt) > timestamp(previous.updatedAt)
    )
      byId.set(id, layout);
  }
  function rootFor(layout: T): T {
    const visited = new Set<string>();
    let current = layout;
    while (current.mainLayoutId) {
      const id = current.id.toLowerCase();
      if (visited.has(id)) return layout;
      visited.add(id);
      const parent = byId.get(current.mainLayoutId.toLowerCase());
      // A missing parent stays visible as its own group. A malformed cycle
      // likewise never makes a supplied layout disappear.
      if (!parent || parent.id.toLowerCase() === id) return current;
      current = parent;
    }
    return current;
  }
  const grouped = new Map<string, LayoutGroup<T>>();
  for (const layout of byId.values()) {
    const main = rootFor(layout);
    const key = main.id.toLowerCase();
    let group = grouped.get(key);
    if (!group) {
      group = { main, subs: [] };
      grouped.set(key, group);
    }
    if (layout.id.toLowerCase() !== key) group.subs.push(layout);
  }
  const groups = [...grouped.values()];
  for (const group of groups)
    group.subs.sort(
      (a, b) =>
        timestamp(a.createdAt) - timestamp(b.createdAt) || compareId(a, b),
    );
  const latest = (group: LayoutGroup<T>) =>
    group.subs.reduce(
      (time, layout) => Math.max(time, timestamp(layout.updatedAt)),
      timestamp(group.main.updatedAt),
    );
  groups.sort((a, b) => latest(b) - latest(a) || compareId(a.main, b.main));
  const term = query.trim().toLocaleLowerCase("ja");
  if (!term) return groups;
  return groups.filter((group) =>
    [group.main, ...group.subs].some((layout) =>
      layout.title.toLocaleLowerCase("ja").includes(term),
    ),
  );
}
